import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useWishlist } from '@/hooks/useWishlist';

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  count: number;
}

interface Product {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);
  const navigate = useNavigate();
  const { count: wishlistCount } = useWishlist();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    supabase.from('categories').select('id,slug,name,icon,count').order('sort_order').then(({ data }) => {
      if (data) setCategories(data);
    });
    supabase.from('products').select('id,name,brand,price,image').order('rating', { ascending: false }).limit(2).then(({ data }) => {
      if (data) setFeatured(data);
    });
  }, []);

  const handleSearch = () => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-ink-100 shadow-soft' : 'bg-white/70 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-4 h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/buywize-logo.png" alt="BuyWize" className="h-8 md:h-9 w-auto" />
          </Link>

          {/* Search */}
          <div className="hidden md:flex flex-1">
            <div className="relative w-full group flex items-center bg-ink-50 border border-ink-200 rounded-full focus-within:bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100 transition h-11">
              <span className="w-11 h-full flex items-center justify-center flex-shrink-0 text-ink-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                type="text"
                placeholder="Search 10,000+ reviewed products, brands, and guides..."
                className="flex-1 h-full pr-5 bg-transparent outline-none text-sm text-ink-800 placeholder:text-ink-400"
              />
            </div>
          </div>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-1 text-sm font-medium text-ink-700">
            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button className="px-3 py-2 rounded-lg hover:bg-ink-50 flex items-center gap-1 whitespace-nowrap transition">
                Categories <i className={`ri-arrow-down-s-line transition ${megaOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {megaOpen && (
                <div className="absolute -left-32 top-full pt-3 w-[880px] animate-fade-in">
                  <div className="glass rounded-2xl shadow-card border border-white/80 p-6 grid grid-cols-3 gap-6">
                    <div className="col-span-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-3">Shop by Category</p>
                      <ul className="space-y-1">
                        {categories.map((c) => (
                          <li key={c.id}>
                            <Link
                              to={`/search?category=${c.slug}`}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-700 hover:bg-white hover:text-brand-600 transition group"
                            >
                              <span className="w-8 h-8 rounded-lg bg-ink-50 group-hover:bg-brand-50 flex items-center justify-center text-brand-600">
                                <i className={`${c.icon} text-base`}></i>
                              </span>
                              <span className="font-medium">{c.name}</span>
                              <span className="ml-auto text-xs text-ink-400">{c.count}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-span-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-3">Featured Products</p>
                      <div className="space-y-3">
                        {featured.map((p) => (
                          <Link to={`/product/${p.id}`} key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white transition group">
                            <div className="w-14 h-14 rounded-lg bg-ink-100 overflow-hidden flex-shrink-0">
                              <img src={p.image} alt={p.name} className="w-full h-full object-contain p-1 group-hover:scale-105 transition" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-ink-900 line-clamp-1">{p.name}</p>
                              <p className="text-xs text-ink-500">{p.brand}</p>
                              <p className="text-sm font-bold text-brand-600 mt-0.5">${p.price}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-1 space-y-3">
                      <Link to="/editors-picks" className="relative rounded-xl overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800 text-white p-5 h-[144px] block">
                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-100">Editor&rsquo;s Picks</p>
                        <p className="mt-1 text-lg font-bold leading-tight">Hand-picked by our team</p>
                        <span className="mt-3 inline-flex h-8 px-3 rounded-md bg-white text-brand-700 text-xs font-bold hover:bg-brand-50 transition whitespace-nowrap items-center justify-center">
                          See Picks
                        </span>
                      </Link>
                      <Link to="/guides" className="relative rounded-xl overflow-hidden bg-gradient-to-br from-cta-500 to-cta-700 text-white p-5 h-[144px] block">
                        <p className="text-xs font-semibold uppercase tracking-wider text-cta-100">New Buying Guide</p>
                        <p className="mt-1 text-lg font-bold leading-tight">Best Wireless Headphones 2026</p>
                        <span className="mt-3 inline-flex h-8 px-3 rounded-md bg-white text-cta-700 text-xs font-bold hover:bg-cta-50 transition whitespace-nowrap items-center justify-center">
                          Read Guide
                        </span>
                        <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/10"></div>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/editors-picks" className="px-3 py-2 rounded-lg hover:bg-ink-50 transition whitespace-nowrap">Editor&rsquo;s Picks</Link>
            <Link to="/search" className="px-3 py-2 rounded-lg hover:bg-ink-50 transition whitespace-nowrap">Reviews</Link>
            <Link to="/guides" className="px-3 py-2 rounded-lg hover:bg-ink-50 transition whitespace-nowrap">Buying Guides</Link>
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1 ml-auto lg:ml-0">
            <Link to="/wishlist" className="w-10 h-10 rounded-full hover:bg-ink-50 flex items-center justify-center text-ink-700 transition relative" aria-label="Wishlist">
              <i className={`${wishlistCount > 0 ? 'ri-heart-fill text-cta-600' : 'ri-heart-line'} text-xl`}></i>
              {wishlistCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-cta-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full hover:bg-ink-50 flex items-center justify-center text-ink-700 transition"
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 animate-fade-in">
            <div className="flex items-center bg-ink-50 border border-ink-200 rounded-full h-11">
              <span className="w-11 h-full flex items-center justify-center flex-shrink-0 text-ink-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                    setMobileOpen(false);
                  }
                }}
                type="text"
                placeholder="Search products..."
                className="flex-1 h-full pr-4 bg-transparent outline-none text-sm text-ink-800 placeholder:text-ink-400"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {categories.map((c) => (
                <Link key={c.id} to={`/search?category=${c.slug}`} className="flex items-center gap-2 p-3 rounded-lg bg-ink-50 hover:bg-ink-100 transition">
                  <i className={`${c.icon} text-brand-600`}></i>
                  <span className="font-medium text-ink-800">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}